import { useEffect, useRef, useState } from 'react';
import { fetchMacro } from '../api';
import { MacroItem } from '../types';

const LABEL_ORDER = [
  'VIX', 'S&P500', '나스닥', 'KOSPI', 'KOSDAQ',
  '원달러', 'DXY', '美10Y', '금', 'WTI', 'BTC',
];
// 나스닥선물·S&P선물은 상단바에서 빼고 '시장 분석' 안으로 이동(상단바 슬림화). 데이터는 /api/macro에 그대로.

// 🎨 규격 V2(08-23) — 밝은 원색 대신 저채도 up/down + ink 램프. 칸 사이 세로선 없이 여백으로만 끊는다.
function MacroCell({ item }: { item: MacroItem }) {
  const isNull = item.value === null || item.change_pct === null;
  const up = (item.change_pct ?? 0) > 0;
  const down = (item.change_pct ?? 0) < 0;

  // 한국식: 상승=웜, 하락=쿨 (경보 빨강 금지)
  const changeColor = up ? 'text-dc-up' : down ? 'text-dc-down' : 'text-dc-ink3';
  const arrow = up ? '▲' : down ? '▼' : '─';
  // 코스피는 이 줄의 기준점 — 한 칸만 밝게 둔다(나머지는 보조 밝기).
  const isAnchor = item.label === 'KOSPI';

  return (
    <span className="flex items-baseline gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-2 whitespace-nowrap text-[11.5px] font-medium">
      <span className="text-dc-ink3">{item.label}</span>
      {isNull ? (
        <span className="text-dc-ink3/70">N/A</span>
      ) : (
        <>
          <span className={isAnchor ? 'text-dc-ink1 font-bold' : 'text-dc-ink2 font-semibold'}>
            {item.label === 'BTC'
              ? Number(item.value).toLocaleString('en-US', { maximumFractionDigits: 0 })
              : Number(item.value).toLocaleString('en-US', { maximumFractionDigits: 2 })}
          </span>
          <span className={changeColor}>
            {arrow}{Math.abs(item.change_pct ?? 0).toFixed(2)}%
          </span>
        </>
      )}
    </span>
  );
}

export default function MacroBar() {
  const [items, setItems] = useState<MacroItem[]>([]);
  const [updatedAt, setUpdatedAt] = useState('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const load = async () => {
    try {
      const data = await fetchMacro();
      const ordered = LABEL_ORDER.map(
        (label) => data.items.find((i) => i.label === label) ?? { label, value: null, change_pct: null }
      );
      setItems(ordered);
      setUpdatedAt(new Date(data.updated_at).toLocaleTimeString('ko-KR'));
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  return (
    <div className="sticky top-0 z-50 bg-dc-bg0 border-b border-dc-hair relative">
      <div className="flex items-center overflow-x-auto no-scrollbar px-1.5 sm:px-2">
        {items.map((item) => (
          <div key={item.label} className="shrink-0">
            <MacroCell item={item} />
          </div>
        ))}
        {updatedAt && (
          <span className="hidden sm:inline text-dc-ink3/80 text-[11.5px] px-3 whitespace-nowrap ml-auto shrink-0">
            갱신 {updatedAt}
          </span>
        )}
      </div>
      {/* 모바일 스크롤 힌트 — 오른쪽이 더 있다는 페이드(좁은 화면에서만) */}
      <div className="pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-dc-bg0 to-transparent sm:hidden" />
    </div>
  );
}
